import { animated, useSpring } from "@react-spring/three";
import { useGLTF } from "@react-three/drei";
import React, { useContext, useEffect } from "react";
import {
  BoltClosePos,
  BoltOpenPosBack,
  BoltOpenPosBottom,
  BoltOpenPosFront,
  BoltOpenPosTop,
  Deg0,
  DoorDeg90CCW,
  HandleDeg90CW,
  Scale0,
  Scale1,
} from "../../utils/AnimaionSprings";
import CurrentContext from "../../utils/CurrentContext";
import useChangedTexture from "../../utils/customHooks/useChangedTexture";
import useIsMobile from "../../utils/customHooks/useIsMobile";
import useTextureCustom from "../../utils/customHooks/useTextureCustom";
import {
  BlackPlastic,
  BlackPowerCoat,
  BoltMaterial,
  PaintMaterial,
} from "../../utils/finsishesData";
import { handleExportGLTF } from "../../utils/jsHelper";
import { LockMechanicalLarge, LockSecuramBacklit } from "./model_locks";

//region Default options
const defaultoptions = [
  {
    materialName: "body_paint",
    finishToApply: "bronzetextured",
  },
  {
    materialName: "interior_fabric",
    finishToApply: "camelfauxleather",
  },
  {
    materialName: "securam_prologic",
    finishToApply: "Lock_securam_toplit",
  },
  {
    materialName: "mechanical_lock",
    finishToApply: "Lock_mechanical_large",
  },
];

const HandleUpdateTexture = (item, materials) => {
  useTextureCustom(materials[item.materialName], item.finishToApply);
};

export function Franklinn50({ animateInit, currentVariant, lockType }) {
  const isMobile = useIsMobile();
  const { nodes, materials, scene } = useGLTF(
    isMobile ? "models/franklinn50-m.glb" : "models/franklinn50.glb");
  const mobileModel = useGLTF("models/franklinn50-m.glb");

  if (isMobile) {
    return (
      <Model
        {...{
          nodes,
          materials,
          scene,
          animateInit,
          currentVariant,
          lockType,
          mobileProp: true,
        }}
      />
    );
  } else {
    return (
      <>
        <Model
          {...{
            nodes,
            materials,
            scene,
            animateInit,
            currentVariant,
            lockType,
          }}
        />
        <Model
          {...{
            nodes: mobileModel?.nodes,
            materials: mobileModel?.materials,
            scene: mobileModel?.scene,
            animateInit,
            currentVariant,
            lockType,
            visible: false,
            mobileProp: true,
          }}
        />
      </>
    );
  }
}

const Model = ({
  nodes,
  materials,
  scene,
  animateInit,
  currentVariant,
  lockType,
  visible = true,
  mobileProp = false,
}) => {
  const {
    setLoading,
    setIsARReady,
    setARModelSrc,
    setARUnicode,
    isAR,
    setIsAR,
    doorOpen,
  } = useContext(CurrentContext);

  //region animation springs
  const { rotation_Door } = useSpring({
    rotation_Door: doorOpen ? DoorDeg90CCW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });

  const { handle_Rotation1 } = useSpring({
    handle_Rotation1: doorOpen ? HandleDeg90CW : Deg0,
    config: { duration: 1000 },
    delay: doorOpen ? 0 : 3250,
  });

  const { handle_Rotation2 } = useSpring({
    handle_Rotation2: doorOpen ? Deg0 : HandleDeg90CW,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { handle_Scale1 } = useSpring({
    handle_Scale1: doorOpen ? Scale0 : Scale1,
    config: { duration: 1 },
    delay: 2250,
  });

  const { handle_Scale2 } = useSpring({
    handle_Scale2: doorOpen ? Scale1 : Scale0,
    config: { duration: 1 },
    delay: 2250,
  });

  const { position_Bolt_Front } = useSpring({
    position_Bolt_Front: doorOpen ? BoltOpenPosFront : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Back } = useSpring({
    position_Bolt_Back: doorOpen ? BoltOpenPosBack : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Top } = useSpring({
    position_Bolt_Top: doorOpen ? BoltOpenPosTop : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Bottom } = useSpring({
    position_Bolt_Bottom: doorOpen ? BoltOpenPosBottom : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  useEffect(() => {
    if (isAR && mobileProp) {
      handleExportGLTF({
        scene,
        fileName: "franklin50",
        setLoading,
      }).then(({ file, unicode } = {}) => {
        setARModelSrc(file);
        setIsARReady(true);
        setARUnicode(unicode);
      });
    }
    setIsAR(false);
  }, [isAR]);

  defaultoptions.map((item) => {
    HandleUpdateTexture(item, materials);
  });

  const materialNames = {
    interior_materials: "interior_fabric",
    outer_materials: "body_paint",
    hardware_materials: ["wheel_handle"],
  };

  useChangedTexture(
    currentVariant,
    materials,
    materialNames,
    HandleUpdateTexture
  );
  useTextureCustom(PaintMaterial, currentVariant.outer);

  const decalOuterColor =
    currentVariant.hardware === "blackchromehardware"
      ? "#515151"
      : currentVariant.hardware === "chromehardware"
      ? "#e5e5e5"
      : currentVariant.hardware === "brasshardware"
      ? "#C1A163"
      : "#515151";

  return (
    <group dispose={null} visible={visible}>
      <animated.group
        position={[0.457, 0.077, 0.348122]}
        rotation={rotation_Door}
      >
        <animated.mesh
          position={position_Bolt_Back}
          castShadow
          geometry={nodes.bolts_back.geometry}
          material={BoltMaterial}
        />
        <animated.mesh
          position={position_Bolt_Front}
          castShadow
          geometry={nodes.bolts_front.geometry}
          material={BoltMaterial}
        />
        <animated.mesh
          position={position_Bolt_Bottom}
          castShadow
          geometry={nodes.bolts_bottom.geometry}
          material={BoltMaterial}
        />
        <animated.mesh
          position={position_Bolt_Top}
          castShadow
          geometry={nodes.bolts_top.geometry}
          material={BoltMaterial}
        />
        <mesh
          castShadow
          geometry={nodes.bolts_housing.geometry}
          material={BlackPlastic}
        />
        {/* Locks */}
        <group position={[-0.456, 1.275, 0.002]}>
          <LockMechanicalLarge
            material={materials.wheel_handle}
            scale={lockType === "mechanicallock" ? [1, 1, 1] : [0, 0, 0]}
            decalOuterColor={decalOuterColor}
          />
          <LockSecuramBacklit
            material={materials.wheel_handle}
            scale={lockType === "securamdirectdrive" ? [1, 1, 1] : [0, 0, 0]}
          />
        </group>
        {/* Door Exterior */}
          <mesh
            castShadow
            geometry={nodes.outer_door.geometry}
            material={PaintMaterial}
          />
        <mesh
          position={[0, 0, 0.005]}
          geometry={nodes.door_decal.geometry}
          material={materials.door_decal}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_1.geometry}
          material={materials.pockets_base}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_2.geometry}
          material={materials.door_bag_strap}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_3.geometry}
          material={materials.door_pockets_1}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_4.geometry}
          // material={materials.door_pockets_1_border}
          material={materials.door_pockets_2}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_5.geometry}
          material={materials.door_pockets_1_border_01}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_6.geometry}
          material={materials.door_pockets_2}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_7.geometry}
          // material={materials.door_pockets_2_border}
          material={materials.door_pockets_2}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_8.geometry}
          material={materials.door_pockets_3}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_9.geometry}
          // material={materials.door_pockets_3_zipper}
          material={materials.door_pockets_2}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_10.geometry}
          material={materials.door_pockets_4}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_11.geometry}
          material={materials.door_pockets_5}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_12.geometry}
          material={materials.door_pockets_logo01}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_13.geometry}
          material={materials.door_zippers_1}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_14.geometry}
          material={materials.pockets_base_lable}
        />

        <animated.group
          position={[-0.453, 0.953, 0.001]}
          rotation={handle_Rotation1}
          scale={handle_Scale1}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_1.geometry}
            material={materials.wheel_handle}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_2.geometry}
            material={materials.wheel_decal}
          />
        </animated.group>

        <animated.group
          position={[-0.453, 0.953, 0.001]}
          rotation={handle_Rotation2}
          scale={handle_Scale2}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_1.geometry}
            material={materials.wheel_handle}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_2.geometry}
            material={materials.wheel_decal}
          />
        </animated.group>
      </animated.group>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior.geometry}
        material={materials.interior_fabric}
      />
      <mesh
        castShadow
        geometry={nodes.outer_body.geometry}
        material={PaintMaterial}
      />

      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_1.geometry}
        material={materials.door_frame_rubber}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_2.geometry}
        material={materials.border}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_3.geometry}
        material={materials.cable_chrome}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_4.geometry}
        material={materials.cable_rubber}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_5.geometry}
        // material={materials.frame}
        material={BlackPowerCoat}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_6.geometry}
        material={materials.knot}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_7.geometry}
        material={materials.screw}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_8.geometry}
        material={materials.stich}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_9.geometry}
        material={materials.support}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_10.geometry}
        // material={materials.plastic}
        material={BlackPlastic}
      />
    </group>
  );
}

//useGLTF.preload("models/franklinn50-v1.glb");
